import {and} from '@edna/utils';

import {TData, TPayload} from 'src/components/List';
import {TItemId} from 'src/constants';
import {EPermission} from 'src/containers/User';
import {CACHE_LIFETIME, ECacheTag, listenerMiddleware, rootApi} from 'src/models';

import {
  EDialogStatus,
  NAME_PARAMETER_CODES,
  TDialog,
  TDialogMessage,
  TDialogSubscriberResponse,
  TGetDialogMessagesPayload,
  TGetDialogsPayload,
  TPollPayload,
  TPollResponse,
  TSendMessagePayload,
  TSendMessageResponse,
  TSendTemplateMessagePayload,
  TSendTemplateMessageResponse,
  TStartDialogPayload,
  TStartDialogResponse,
  TStartTemplateDialogPayload,
} from './definitions';
import {setSubscribersAction} from './slice';

const api = rootApi.injectEndpoints({
  overrideExisting: 'throw',
  endpoints: (builder) => ({
    getDialog: builder.query<TDialog, TItemId>({
      query: (id) => ({
        method: 'GET',
        url: `/dialogs/${id}`,
        meta: {
          permissions: and<EPermission>('PERMISSION_DIALOG'),
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: (_, __, id) => [{id, type: ECacheTag.DIALOGS}],
    }),
    getDialogs: builder.query<TData<TDialog>, TGetDialogsPayload>({
      query: (data) => ({
        method: 'POST',
        url: '/dialogs',
        data,
        meta: {
          permissions: and<EPermission>('PERMISSION_DIALOG'),
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: (result) => {
        if (!result) {
          return [ECacheTag.DIALOGS];
        }

        return [
          ECacheTag.DIALOGS,
          ...result.content.map(({id}) => ({id, type: ECacheTag.DIALOGS})),
        ];
      },
    }),

    getDialogMessages: builder.query<TData<TDialogMessage>, TPayload<TGetDialogMessagesPayload>>({
      query: ({dialogId, offset, limit}) => ({
        method: 'GET',
        url: `/dialogs/${dialogId}/messages`,
        params: {offset, limit},
        meta: {
          permissions: and<EPermission>('PERMISSION_DIALOG'),
        },
      }),
      keepUnusedDataFor: CACHE_LIFETIME,
      providesTags: (result, _, {dialogId}) => {
        if (!result) {
          return [{type: ECacheTag.DIALOG_MESSAGES, id: dialogId}];
        }

        return [
          {type: ECacheTag.DIALOG_MESSAGES, id: dialogId},
          ...result.content.map(({id}) => ({id, type: ECacheTag.DIALOG_MESSAGES})),
        ];
      },
    }),

    pollDialogs: builder.query<TPollResponse, TPollPayload>({
      query: (data) => ({
        method: 'POST',
        url: '/dialogs/poll',
        data,
        meta: {
          permissions: and<EPermission>('PERMISSION_DIALOG'),
        },
      }),
      keepUnusedDataFor: 0,
    }),

    sendMessage: builder.mutation<TSendMessageResponse, TSendMessagePayload>({
      query: ({dialogId, content}) => ({
        method: 'POST',
        url: `/dialogs/${dialogId}/messages`,
        data: {content},
        meta: {
          isShowError: true,
          permissions: and<EPermission>('PERMISSION_DIALOG'),
        },
      }),
      invalidatesTags: (_, __, {dialogId}) => [
        {type: ECacheTag.DIALOG_MESSAGES, id: dialogId},
        ECacheTag.DIALOGS,
      ],
    }),

    sendTemplateMessage: builder.mutation<
      TSendTemplateMessageResponse,
      TSendTemplateMessagePayload
    >({
      query: ({dialogId, content}) => ({
        method: 'POST',
        url: `/dialogs/${dialogId}/template`,
        data: {content},
        meta: {
          isShowError: true,
          permissions: and<EPermission>('PERMISSION_DIALOG'),
        },
      }),
      invalidatesTags: (_, __, {dialogId}) => [
        {type: ECacheTag.DIALOG_MESSAGES, id: dialogId},
        ECacheTag.DIALOGS,
      ],
    }),

    startDialog: builder.mutation<TStartDialogResponse, TStartDialogPayload>({
      query: (data) => ({
        method: 'POST',
        url: '/dialogs/start',
        data,
        meta: {
          isShowError: true,
          permissions: and<EPermission>('PERMISSION_DIALOG'),
        },
      }),
      invalidatesTags: [ECacheTag.DIALOGS],
    }),

    startTemplateDialog: builder.mutation<
      TSendTemplateMessageResponse,
      TStartTemplateDialogPayload
    >({
      query: (data) => ({
        method: 'POST',
        url: '/dialogs/start/template',
        data,
        meta: {
          isShowError: true,
          permissions: and<EPermission>('PERMISSION_DIALOG'),
        },
      }),
      invalidatesTags: [ECacheTag.DIALOGS],
    }),

    updateDialogStatus: builder.mutation<void, {dialogId: TItemId; status: EDialogStatus}>({
      query: ({dialogId, status}) => ({
        method: 'PATCH',
        url: `/dialogs/${dialogId}/status`,
        data: {status},
        meta: {
          permissions: and<EPermission>('PERMISSION_DIALOG'),
        },
      }),
      invalidatesTags: [ECacheTag.DIALOGS],
    }),

    getDialogSubscribers: builder.query<
      TDialogSubscriberResponse[],
      {address: string; type: string}[]
    >({
      query: (addresses) => ({
        method: 'POST',
        url: `/subscriber/by-addresses`,
        data: {
          addresses,
          parameterCodes: NAME_PARAMETER_CODES,
        },
      }),
    }),
  }),
});

listenerMiddleware.startListening({
  matcher: api.endpoints.pollDialogs.matchFulfilled,
  effect: ({payload}, listenerApi) => {
    if (!payload.timeout) {
      listenerApi.dispatch(api.util.invalidateTags([ECacheTag.DIALOGS, ECacheTag.DIALOG_MESSAGES]));
    }
  },
});

listenerMiddleware.startListening({
  matcher: api.endpoints.getDialogs.matchFulfilled,
  effect: ({payload: dialogsData}, {dispatch}) => {
    if (!dialogsData?.content?.length) {
      return;
    }

    const subscribersPayload = dialogsData.content.map((dialog) => ({
      address: dialog.subscriberAddress,
      type: dialog.subscriberAddressType,
    }));

    dispatch(api.endpoints.getDialogSubscribers.initiate(subscribersPayload));
  },
});

listenerMiddleware.startListening({
  matcher: api.endpoints.getDialogSubscribers.matchFulfilled,
  effect: ({payload: subscribers}, {dispatch}) => {
    if (!subscribers?.length) {
      return;
    }

    dispatch(setSubscribersAction(subscribers));
  },
});

export const {
  useGetDialogsQuery,
  useGetDialogMessagesQuery,
  usePollDialogsQuery,
  useSendMessageMutation,
  useSendTemplateMessageMutation,
  useStartDialogMutation,
  useStartTemplateDialogMutation,
  useUpdateDialogStatusMutation,
  useLazyGetDialogMessagesQuery,
  useGetDialogQuery,
  useLazyGetDialogSubscribersQuery,
} = api;
